// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getBillToState, getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { getShipTosDataView, getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateShipTo from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/UpdateShipTo";
import translate from "@insite/client-framework/Translate";
import { BaseAddressModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomerAddressForm, { CustomerAddressFormStyles } from "@west/Overrides/Components/CustomerAddressForm";
import ShipToSelector, { ShipToSelectorStyles } from "@west/Overrides/Components/ShipToSelector";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    isOpen: boolean;
    onModalClose: () => void;
    extendedStyles?: ShipToSelectorModalStyles;
    /**
     * XNG-Changes
     */
    isShippingValidation?: boolean;
}

type Props = OwnProps &
    HasHistory &
    HasToasterContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCurrentCartState(state).value;
    return {
        cart,
        currentShipTo: getShipToState(state, cart ? cart.shipToId : undefined).value,
        shipTosDataView: cart
            ? getShipTosDataView(state, { billToId: cart.billToId, expand: ["validation"], exclude: ["showAll"] })
            : undefined,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        countries: getCurrentCountries(state),
        shipToAddressFields: getAddressFieldsDataView(state).value?.shipToAddressFields,
        currentBillTo: getBillToState(state, cart?.billToId).value,
    };
};

const mapDispatchToProps = {
    updateShipTo,
};

export interface ShipToSelectorModalStyles {
    modal?: ModalPresentationProps;
    shipToSelector?: ShipToSelectorStyles;
    buttonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    addCustomerAddressForm?: CustomerAddressFormStyles;
    editCustomerAddressForm?: CustomerAddressFormStyles;
}

export const shipToSelectorModalStyles: ShipToSelectorModalStyles = {
    modal: { sizeVariant: "medium" },
    buttonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            margin-top: 1rem;
        `,
    },
    cancelButton: { variant: "secondary" },
};

const ShipToSelectorModal = ({
    cart,
    currentShipTo,
    shipTosDataView,
    cartPageUrl,
    countries,
    shipToAddressFields,
    isOpen,
    extendedStyles,
    history,
    toaster,
    updateShipTo,
    onModalClose,
    isShippingValidation,
    // XNG-Changes
    currentBillTo,
}: Props) => {
    const [styles] = useState(() => mergeToNew(shipToSelectorModalStyles, extendedStyles));

    const [shiToBeingEdited, setShipToBeingEdited] = useState<ShipToModel | undefined>(undefined);
    const [isShipToCreating, setIsShipToCreating] = useState(false);

    let newAddress;
    if (cart && cart.cartLines && shipTosDataView && shipTosDataView.value) {
        newAddress = shipTosDataView.value!.find(s => s.isNew && !s.oneTimeAddress);
    }

    const selectShipToHandler = (shipTo: ShipToModel) => {
        onModalClose();
        updateShipTo({
            billToId: cart?.billToId || "",
            shipTo,
            isEdit: false,
            onSuccess: hasCartlines => {
                if (!hasCartlines && cartPageUrl) {
                    history.push(cartPageUrl);
                }
            },
        });
    };

    const editShipToHandler = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, customer: BaseAddressModel) => {
        event.preventDefault();
        setShipToBeingEdited(customer as ShipToModel);
    };

    const createNewAddressHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();
        setIsShipToCreating(true);
    };
    // XNG-Changes: change address type to any because of customer addressForm generic type issue
    const newShipToFormSubmitHandler = (_: React.FormEvent<HTMLFormElement>, address: any) => {
        updateShipTo({
            billToId: cart?.billToId || "",
            shipTo: address,
            isEdit: true,
            onSuccess: hasCartlines => {
                toaster.addToast({
                    messageType: "success",
                    body: translate("Address has been created."),
                });
                if (!hasCartlines && cartPageUrl) {
                    history.push(cartPageUrl);
                }
            },
        });

        setIsShipToCreating(false);
        onModalClose();
    };

    const createNewAddressCancelHandler = () => {
        setIsShipToCreating(false);
    };

    // XNG-Changes: change address type to any because of customer addressForm generic type issue
    const editShipToFormSubmitHandler = (_: React.FormEvent<HTMLFormElement>, shipTo: any) => {
        updateShipTo({
            billToId: cart?.billToId || "",
            shipTo,
            isEdit: true,
            onSuccess: hasCartlines => {
                toaster.addToast({
                    messageType: "success",
                    body: translate("Address updated."),
                });
                if (!hasCartlines && cartPageUrl) {
                    history.push(cartPageUrl);
                }
            },
        });
        setShipToBeingEdited(undefined);
        onModalClose();
    };

    const editAddressCancelHandler = () => {
        setShipToBeingEdited(undefined);
    };

    const submitDeactivateShipToHandler = (e: any, shipTo: ShipToModel, closeModal: () => void) => {
        e.preventDefault();
        if (!cart || !cart.billToId || !shipTo) {
            return;
        }

        updateShipTo({
            billToId: cart.billToId,
            shipTo: {
                ...shipTo,
                properties: {
                    ...shipTo.properties,
                    isActive: false,
                } as any,
            },
            isEdit: true,
            onSuccess: () => {
                closeModal();
                toaster.addToast({
                    body: translate("Address deleted successfully"),
                    messageType: "success",
                    timeoutLength: 5000,
                });
            },
            ...({
                onError: (result: any) => {
                    closeModal();
                    toaster.addToast({
                        body: translate("Error in deleting the address"),
                        messageType: "danger",
                        timeoutLength: 5000,
                    });
                },
            } as any),
        });
    };

    /**
     * XNG-Changes
     * pass isShippingValidation
     */
    return (
        <Modal
            {...styles.modal}
            headline={
                isShipToCreating
                    ? translate("Create New Address")
                    : shiToBeingEdited
                    ? translate("Edit Address")
                    : translate("Address Book")
            }
            isOpen={isOpen}
            handleClose={onModalClose}
            data-test-selector="onePageCheckoutShipping_addressModal"
        >
            {!shiToBeingEdited && !isShipToCreating && (
                <>
                    <ShipToSelector
                        currentShipTo={currentShipTo}
                        currentBillToId={cart?.billToId}
                        newAddress={newAddress}
                        onSelect={selectShipToHandler}
                        onEdit={editShipToHandler}
                        onCreateNewAddressClick={newAddress && createNewAddressHandler}
                        allowSelectBillTo={true}
                        extendedStyles={styles.shipToSelector}
                        // XNG-Changes:
                        onDelete={submitDeactivateShipToHandler}
                        currentBillTo={currentBillTo}
                    />
                    <StyledWrapper {...styles.buttonsWrapper}>
                        <Button {...styles.cancelButton} onClick={onModalClose}>
                            {translate("Cancel")}
                        </Button>
                    </StyledWrapper>
                </>
            )}
            {shiToBeingEdited && countries && shipToAddressFields && (
                <CustomerAddressForm
                    address={shiToBeingEdited}
                    countries={countries}
                    addressFieldDisplayCollection={shipToAddressFields}
                    onSubmit={editShipToFormSubmitHandler}
                    saveButtonTextOverride={translate("Save & Apply")}
                    onCancel={editAddressCancelHandler}
                    extendedStyles={styles.editCustomerAddressForm}
                    isShippingValidation={isShippingValidation}
                />
            )}
            {isShipToCreating && newAddress && countries && shipToAddressFields && (
                <CustomerAddressForm
                    address={newAddress}
                    countries={countries}
                    addressFieldDisplayCollection={shipToAddressFields}
                    onSubmit={newShipToFormSubmitHandler}
                    onCancel={createNewAddressCancelHandler}
                    extendedStyles={styles.addCustomerAddressForm}
                    isShippingValidation={isShippingValidation}
                />
            )}
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withToaster(ShipToSelectorModal)));
